import { useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext, useLocation } from 'react-router-dom';

type Navigator = {
  push: (to: unknown, ...rest: unknown[]) => void;
  replace: (to: unknown, ...rest: unknown[]) => void;
};

/** The pathname a `navigator.push`/`replace` target leads to (targets arrive resolved). */
const targetPathname = (to: unknown, current: string) => {
  if (typeof to === 'string')
    return new URL(to, `${globalThis.location.origin}${current}`).pathname;
  const pathname = (to as { pathname?: string } | null)?.pathname;
  return pathname || current;
};

/** Wraps `navigator.push`/`replace`: a navigation that `passes` goes at once, any other asks. */
const guardNavigation =
  (
    navigator: Navigator,
    go: Navigator['push'],
    passes: (to: unknown) => boolean,
    ask: (proceed: () => void) => void
  ) =>
  (to: unknown, ...rest: unknown[]) => {
    if (passes(to)) {
      go.call(navigator, to, ...rest);
      return;
    }
    ask(() => go.call(navigator, to, ...rest));
  };

/** The router's index of the current browser history entry (set by react-router's history). */
const historyIndex = (): number | null => {
  const idx = (globalThis.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' ? idx : null;
};

/**
 * Asks before unsaved soul changes are lost, at the router: every `navigate()` and `<Link>` to
 * another page (they all go through the router's navigator), the browser's Back and Forward
 * buttons, and a reload or tab close (the browser's own prompt). Changes of the search or hash on
 * the same page pass. The app uses a `BrowserRouter`, so react-router's `useBlocker` is not
 * available; the navigator is wrapped only while the draft is dirty and restored afterwards.
 */
export function useSoulLeaveGuard(isDirty: boolean, onDiscard: () => void) {
  const { navigator } = useContext(UNSAFE_NavigationContext) as unknown as {
    navigator: Navigator;
  };
  const { pathname, key: locationKey } = useLocation();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  // The router's index of the entry on screen, kept current through same-page navigations.
  const indexRef = useRef<number | null>(null);
  useEffect(() => {
    indexRef.current = historyIndex();
  }, [locationKey]);
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
  const releaseRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isDirty) return undefined;
    const { push, replace } = navigator;
    const ask = (proceed: () => void) => setPendingLeave(() => proceed);
    // A released wrapper that is still in a chain (another one was installed over it) passes through.
    let released = false;
    const passes = (to: unknown) =>
      released || targetPathname(to, pathnameRef.current) === pathnameRef.current;
    const guardedPush = guardNavigation(navigator, push, passes, ask);
    const guardedReplace = guardNavigation(navigator, replace, passes, ask);
    navigator.push = guardedPush;
    navigator.replace = guardedReplace;

    // Back/Forward: the URL has already changed when popstate fires. This listener runs before
    // the router's own (capture listeners run first at the target) and hides the event from it,
    // then steps back to where the user was; leaving replays the step. A step within the same
    // page passes, like a same-page push.
    let isRestoring = false;
    const onPopState = (event: PopStateEvent) => {
      if (isRestoring) {
        isRestoring = false;
        event.stopImmediatePropagation();
        return;
      }
      const index = indexRef.current;
      const nextIndex = historyIndex();
      if (index === null || nextIndex === null) return;
      const delta = nextIndex - index;
      if (delta === 0 || globalThis.location.pathname === pathnameRef.current) return;
      event.stopImmediatePropagation();
      isRestoring = true;
      globalThis.history.go(-delta);
      ask(() => globalThis.history.go(delta));
    };
    // preventDefault alone shows the browser's prompt in current browsers.
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    globalThis.addEventListener('popstate', onPopState, true);
    globalThis.addEventListener('beforeunload', onBeforeUnload);

    const release = () => {
      if (released) return;
      released = true;
      // Only our own wrappers are taken off, never one installed over them.
      if (navigator.push === guardedPush) navigator.push = push;
      if (navigator.replace === guardedReplace) navigator.replace = replace;
      globalThis.removeEventListener('popstate', onPopState, true);
      globalThis.removeEventListener('beforeunload', onBeforeUnload);
    };
    releaseRef.current = release;
    return () => {
      release();
      if (releaseRef.current === release) releaseRef.current = null;
    };
  }, [isDirty, navigator]);

  return {
    isLeaveDialogOpen: pendingLeave !== null,
    cancelLeave: () => setPendingLeave(null),
    /** Drops the guard and the draft first, so the navigation (and any later one) goes through. */
    confirmLeave: () => {
      const proceed = pendingLeave;
      setPendingLeave(null);
      releaseRef.current?.();
      onDiscard();
      proceed?.();
    },
  };
}
